/**
 * Hooks for the manual "Send packet" action on the claim detail page.
 *
 * - `useSendPacketPreview(claimUuid)` - calls the backend preflight to
 *   resolve which email packet (template) the system would dispatch
 *   for the claim's current state and predicate context. Returns
 *   `null` when no trigger matches (the button should stay hidden).
 *
 * - `useSendPacket()` - mutation that POSTs the admin-picked recipients
 *   to the send endpoint. System always picks the template; admin only
 *   picks WHO.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import api from '@/admin/lib/api'

export type RecipientKind = 'consumer' | 'contractor' | 'third_party' | string

export interface SendPacketPreview {
  template: {
    id: number
    slug: string
    name: string
    kind: string
    subject: string
  }
  trigger_uuid: string
  available_recipient_kinds: RecipientKind[]
  /** kind -> 'internal' | 'external' | 'unknown'. Platform taxonomy, served. */
  recipient_audiences?: Record<string, 'internal' | 'external' | 'unknown'>
  /** kind -> the tenant's own word for it, from their entity labels. */
  recipient_labels?: Record<string, string>
  /** Everyone the claim names: customer, contractor, attribute parties, contacts. */
  claim_parties?: ClaimParty[]
  /** Whether the tenant has SMS enabled (controls the "also text" option). */
  sms_available?: boolean
  /** Whether letters can go by post: the tenant has a mailroom inbox. */
  mail_available?: boolean
}

/** One addressee the claim itself names, resolved on the server. */
export interface ClaimParty {
  /** Stable key the send resolves again: customer / contractor / attr:<x> / contact:<uuid>. */
  key: string
  kind: string
  /** The tenant's word for who this is, e.g. Homeowner or a contact role. */
  label: string
  name: string
  email: string
  /** Postal address, one line per part. Empty when the claim holds none. */
  address: string
  audience: 'internal' | 'external'
}

export interface SendPacketRecipient {
  kind: RecipientKind
  /** Required when kind = 'third_party'. */
  email?: string
  /** Optional admin-supplied display name (third_party). */
  name?: string
  /** Where to post it (third_party, by Mail). */
  postal_address?: string
}

/** How a letter reaches its addressee. */
export type LetterDelivery = 'email' | 'mail'

export interface SendPacketResultRow {
  kind: RecipientKind
  email: string
  name: string
  matched: boolean
  suppressed: boolean
  suppressed_reason: string | null
  communication_log_id: number | null
  upload_session_id: number | null
  error: string | null
}

export interface SendPacketSmsResult {
  status: string
  reason: string
  communication_log_id: number | null
}

export interface SendPacketResponse {
  template_slug: string
  results: SendPacketResultRow[]
  sms?: SendPacketSmsResult | null
}

export function useSendPacketPreview(
  claimUuid: string | undefined,
  /** Compose a chosen letter instead of the one the claim's state resolves to. */
  templateId?: number | null,
) {
  return useQuery<SendPacketPreview | null>({
    queryKey: ['claim-send-packet-preview', claimUuid, templateId ?? null],
    enabled: !!claimUuid,
    queryFn: async () => {
      try {
        const res = await api.get(
          `/claims/claims/${claimUuid}/send-packet/preview/`,
          templateId ? { params: { template_id: templateId } } : undefined,
        )
        return res.data
      } catch (err: unknown) {
        // 409 = no trigger configured for this claim's state/defect.
        // That's a normal "button-disabled" state, not an error to
        // surface - return null and let the UI hide the button.
        const status =
          (err as { response?: { status?: number } })?.response?.status
        if (status === 409) return null
        throw err
      }
    },
  })
}

export interface SendPacketOptions {
  /** Toast on success; defaults to the packet wording. */
  successMessage?: (sent: number) => string
  /** Leave error toasts to the caller, e.g. to highlight fields. */
  handleErrors?: boolean
}

export function useSendPacket(
  claimUuid: string | undefined,
  options: SendPacketOptions = {},
) {
  const qc = useQueryClient()
  const { successMessage, handleErrors = true } = options
  return useMutation<
    SendPacketResponse,
    unknown,
    {
      recipients: SendPacketRecipient[]
      send_sms?: boolean
      template_id?: number | null
      edit?: LetterEdit
      /** Parties picked from the claim; the server resolves their addresses. */
      party_keys?: string[]
      /** Answers to the template's fill-ins, keyed by prompt key. */
      prompt_values?: Record<string, string>
      /** Files to send with a letter: the claim's own, or uploads for this send. */
      attachment_uuids?: string[]
      /** Mail posts the letter through the tenant's mailroom. */
      delivery?: LetterDelivery
    }
  >({
    mutationFn: async ({
      recipients,
      send_sms,
      template_id,
      edit,
      party_keys,
      prompt_values,
      attachment_uuids,
      delivery,
    }) => {
      const res = await api.post(`/claims/claims/${claimUuid}/send-packet/`, {
        recipients,
        send_sms,
        ...(template_id ? { template_id } : {}),
        ...(edit?.subject_override
          ? { subject_override: edit.subject_override }
          : {}),
        ...(edit?.body_html_override
          ? { body_html_override: edit.body_html_override }
          : {}),
        ...(party_keys?.length ? { party_keys } : {}),
        ...(prompt_values && Object.keys(prompt_values).length
          ? { prompt_values }
          : {}),
        ...(attachment_uuids?.length ? { attachment_uuids } : {}),
        ...(delivery === 'mail' ? { delivery } : {}),
      })
      return res.data
    },
    onSuccess: (data) => {
      const sent = data.results.filter(
        (r) => r.matched && !r.suppressed,
      ).length
      const suppressed = data.results.filter((r) => r.suppressed).length
      const failed = data.results.filter((r) => r.error).length

      const sms = data.sms
      const smsSent = sms?.status === 'sent'
      const smsNote = sms
        ? smsSent
          ? ' Customer also texted.'
          : ` SMS not sent (${sms.reason}).`
        : ''

      if (data.results.length === 0 && sms) {
        // SMS-only send (no email recipients picked).
        if (smsSent) toast.success('Upload link texted to the customer.')
        else toast.error(`SMS not sent (${sms.reason}).`)
      } else if (failed) {
        toast.error(
          `Sent ${sent} of ${data.results.length} - ${failed} failed. ` +
            'Check the claim activity for details.',
        )
      } else if (suppressed) {
        toast.success(
          `Sent ${sent}. ${suppressed} suppressed (cooldown / already sent recently).${smsNote}`,
        )
      } else if (successMessage) {
        toast.success(`${successMessage(sent)}${smsNote}`)
      } else {
        toast.success(`Email packet sent to ${sent} recipient(s).${smsNote}`)
      }
      // CommunicationLog likely updated - invalidate any claim activity feed.
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
      qc.invalidateQueries({ queryKey: ['communications-activity'] })
      // A sent letter files a note on the claim.
      qc.invalidateQueries({ queryKey: ['record-notes', 'claim', claimUuid] })
    },
    onError: () => {
      if (handleErrors) toast.error("Couldn't send the email packet. Try again.")
    },
  })
}

export interface ReissueChannelResult {
  /** 'sent' | 'failed' | 'skipped'. */
  status: string
  reason?: string
  communication_log_id?: number | null
}

export interface ReissueUploadLinkResponse {
  /** How many active upload sessions were revoked. */
  revoked: number
  /** The freshly-minted upload link every channel now carries. */
  upload_session_url: string
  /** Where the link was emailed. */
  recipient_email: string
  /** Email delivery outcome (email is always attempted). */
  email: ReissueChannelResult
  /** SMS delivery outcome, or null when SMS was not requested. */
  sms: ReissueChannelResult | null
}

export interface ReissueUploadLinkInput {
  /** Recipient email (defaults to the customer on file, admin-overridable). */
  recipient_email: string
  /** Also text the link (honoured only when the tenant has SMS enabled). */
  send_sms?: boolean
  /** Mobile number for the SMS (defaults to the customer's on file). */
  sms_contact?: string
}

/**
 * Revoke the claim's current evidence upload link, mint a fresh one, and
 * deliver it - the "the link reached the wrong person" case. Email is always
 * sent; SMS is an optional add-on. The shared-session resolver means every
 * channel reissues to this same new link.
 */
export function useReissueUploadLink(claimUuid: string | undefined) {
  const qc = useQueryClient()
  return useMutation<
    ReissueUploadLinkResponse,
    unknown,
    ReissueUploadLinkInput
  >({
    mutationFn: async (input) => {
      const res = await api.post(
        `/claims/claims/${claimUuid}/reissue-upload-link/`,
        input,
      )
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['claim', claimUuid] })
      // WPS-142: the new link's email and the reminder it restarts show at
      // once, not on the next reload.
      qc.invalidateQueries({
        queryKey: ['communications-activity', 'record', 'claim', claimUuid],
      })
      qc.invalidateQueries({ queryKey: ['auto-reminder', 'claim', claimUuid] })
    },
  })
}

// ── Letter library, scoped to one claim ─────────────────────────────

/** Which kind of template the composer is sending. */
export type ComposeKind = 'letter' | 'evidence_request' | 'notification'

/** A compose-time fill-in, referenced in the template as `{{ prompts.<key> }}`. */
export interface LetterPrompt {
  key: string
  label: string
  type?: 'text' | 'number' | 'enum'
  options?: string[]
  required?: boolean
}

export interface ClaimLetterTemplate {
  id: number
  slug: string
  name: string
  subject: string
  kind: string
  tags: string[]
  output_modes: string[]
  prompts: LetterPrompt[]
  /** The tenant's own word for when this letter is used, from their workflow. */
  when_label: string
  /** Whether this letter leaves the tenant. Served, never inferred here. */
  audience: 'internal' | 'external' | 'unknown'
  is_tenant_owned: boolean
  is_default: boolean
}

export interface ClaimLetterGroup {
  key: string
  label: string
  items: ClaimLetterTemplate[]
}

export interface ClaimLetterTemplates {
  kind?: string
  default_template_id: number | null
  groups: ClaimLetterGroup[]
}

/**
 * Templates of one kind this claim can send: the ones the tenant wired to its
 * workflow first, then the rest of the library. Grouping and the "when to
 * use" labels are the tenant's configuration, so no claim type is named here.
 */
export function useClaimLetterTemplates(
  claimUuid: string | undefined,
  kind?: ComposeKind,
) {
  return useQuery<ClaimLetterTemplates>({
    queryKey: ['claim-letter-templates', claimUuid, kind ?? 'all'],
    enabled: !!claimUuid,
    queryFn: async () => {
      const res = await api.get(
        `/claims/claims/${claimUuid}/letter-templates/`,
        kind ? { params: { kind } } : undefined,
      )
      return res.data ?? { default_template_id: null, groups: [] }
    },
  })
}

export interface ClaimLetterPreview {
  template_id: number
  kind?: string
  subject: string
  body_html: string
  body_text: string
  /** The letter PDF, base64. Empty for anything that is not a letter. */
  pdf_base64?: string
  /** The authored source, for the editor to start from. */
  source_subject: string
  source_body_html: string
}

/** A sender's one-off edit. Applies to this letter only, never the template. */
export interface LetterEdit {
  subject_override?: string
  body_html_override?: string
}

/**
 * The chosen letter rendered against this claim, without sending it.
 *
 * Read-before-send: the user signing off on a letter checks the customer's
 * name and the wording here rather than in the sent log.
 */
export function useClaimLetterPreview(
  claimUuid: string | undefined,
  templateId: number | null,
  recipient?: { partyKey?: string; name?: string },
  edit?: LetterEdit,
  promptValues?: Record<string, string>,
) {
  const params: Record<string, string | Record<string, string>> = {}
  // A picked party is addressed from the claim; a typed name only greets.
  if (recipient?.partyKey) params.party_key = recipient.partyKey
  else if (recipient?.name) params.recipient_name = recipient.name
  if (edit?.subject_override) params.subject_override = edit.subject_override
  if (edit?.body_html_override) params.body_html_override = edit.body_html_override
  if (promptValues && Object.keys(promptValues).length) {
    params.prompt_values = promptValues
  }
  return useQuery<ClaimLetterPreview>({
    queryKey: ['claim-letter-preview', claimUuid, templateId, params],
    enabled: !!claimUuid && !!templateId,
    queryFn: async () => {
      // POST, not GET: an edited letter does not fit in a query string. Most of
      // a tenant's stored letters already exceed the usual 2,048-character URL
      // limit unedited, and the failure is a 414 or a silently truncated body -
      // a preview that is not what would send.
      const res = await api.post(
        `/claims/claims/${claimUuid}/letter-templates/${templateId}/preview/`,
        params,
      )
      return res.data
    },
  })
}

export interface SendAttachmentUpload {
  uuid: string
  file_name: string
  file_size: number
}

/**
 * Upload a file to go with one letter. It is kept with the letter's note, not
 * filed on the claim, and only its uploader can attach it (checked on send).
 */
export function useUploadSendAttachment() {
  return useMutation<SendAttachmentUpload, unknown, File>({
    mutationFn: async (file) => {
      const form = new FormData()
      form.append('file', file)
      form.append('use_case', 'CLAIM_PACKET')
      const res = await api.post('/files/upload/', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      const data = res.data as { uuid: string; file_name?: string; file_size?: number }
      return {
        uuid: data.uuid,
        file_name: data.file_name || file.name,
        file_size: data.file_size ?? file.size,
      }
    },
  })
}
